import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumbs from "../common/Breadcrumbs";
import ImageWithBasePath from "../../core/data/img/ImageWithBasePath";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import Aos from "aos";
import { CarDto } from "../../apiClient";
import { CarService } from "../../api/services/car.service";
import { InquiryService } from "../../api/services/inquiry.service";
import { toast } from "react-toastify";
import { LinearProgress } from "@mui/material";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";

const ListingDetails = () => {
  const { id } = useParams();
  const [car, setCar] = useState<CarDto | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Inquiry State
  const [inquiryData, setInquiryData] = useState({
    clientName: '',
    clientPhone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    Aos.init({ duration: 1200, once: true });
    
    const fetchCarData = async () => {
      try {
        if (id) {
          const carData = await CarService.getById(id);
          setCar(carData);
        }
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch car data", error);
        setLoading(false);
      }
    };

    fetchCarData();
  }, [id]);

  const handleInquiryChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setInquiryData(prev => ({ ...prev, [name]: value }));
  };

  const submitInquiry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!car?.id) return;
    
    setSubmitting(true);
    try {
      await InquiryService.submit({
        car: { id: car.id },
        ...inquiryData
      });
      toast.success("Mesajul dvs. a fost trimis cu succes! Veți fi contactat în curând.");
      setInquiryData({ clientName: '', clientPhone: '', message: '' });
    } catch (error) {
        console.error(error);
      toast.error("A apărut o eroare la trimiterea mesajului.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
        <div className="main-wrapper listing-details-modern">
          <div className="loading-modern">
            <div className="loading-spinner-modern">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <p>Se încarcă...</p>
          </div>
        </div>
    )
  }

  if (!car) {
      return (
        <div className="main-wrapper listing-details-modern">
          <div className="container">
            <div className="not-found-modern">
              <i className="feather-alert-circle"></i>
              <h2>Mașina nu a fost găsită</h2>
              <Link to="/" className="btn-modern btn-modern-primary">
                Înapoi la Acasă
              </Link>
            </div>
          </div>
        </div>
      )
  }

  return (
    <div className="main-wrapper listing-details-modern">
      {/* Hero Header */}
      <section className="listing-hero-modern" data-aos="fade-down">
        <div className="hero-overlay-modern"></div>
        <div className="container">
          <div className="listing-hero-content">
            <div className="hero-badge-modern" data-aos="fade-right" data-aos-delay="100">
              <span className="year-badge">{car?.features?.year}</span>
              {car.discountAmount && car.discountAmount > 0 && (
                <span className="discount-badge">-€{car.discountAmount}</span>
              )}
            </div>
            <h1 data-aos="fade-up" data-aos-delay="200">{car.make} {car.model}</h1>
            <div className="hero-quick-specs" data-aos="fade-up" data-aos-delay="300">
              <span><i className="feather-activity"></i> {car.features?.mileage} km</span>
              <span><i className="feather-droplet"></i> {car.features?.fuelType}</span>
              <span><i className="feather-settings"></i> {car.transmission}</span>
              <span><i className="feather-zap"></i> {car.features?.power} CP</span>
            </div>
          </div>
        </div>
      </section>
        <section className="section-modern listing-content-modern">
          <div className="container">
            <div className="row">
              <div className="col-lg-8">
                <div className="detail-product-modern" data-aos="fade-up">
                  <div className="product-img-modern">
                    {/* Image Carousel */}
                    {(() => {
                        const getImageUrl = (img?: any) => {
                            if (!img) return "assets/img/cars/car-01.jpg";
                            
                            // If it's a legacy string URL (from car.image)
                            if (typeof img === 'string') {
                                if (img.startsWith('http')) return img;
                                return img || "assets/img/cars/car-01.jpg";
                            }
                            
                            // If it's a CarImageDto with an ID
                            if (img.id) {
                                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
                                return `${apiUrl}/api/v1/public/car/image/${img.id}`;
                            }

                             // Fallback if we somehow have an object but no ID (shouldn't happen with correct types)
                             return "assets/img/cars/car-01.jpg";
                        };

                        const galleryImages = (car.images && car.images.length > 0) 
                            ? car.images.map(img => {
                                const url = getImageUrl(img);
                                return { 
                                    original: url, 
                                    thumbnail: url, 
                                    originalAlt: img.fileName, 
                                    thumbnailAlt: img.fileName 
                                };
                            })
                            : [{ 
                                original: car.image ? getImageUrl(car.image) : "assets/img/cars/car-01.jpg", 
                                thumbnail: car.image ? getImageUrl(car.image) : "assets/img/cars/car-01.jpg" 
                            }];
                        
                        return <ImageGallery items={galleryImages} showPlayButton={false} showFullscreenButton={true} showNav={true} showThumbnails={galleryImages.length > 1} />;
                    })()}
                  </div>
                </div>
                {/* Specificații */}
                <div className="specs-card-modern" data-aos="fade-up" data-aos-delay="100">
                  <div className="card-header-modern">
                    <i className="feather-list"></i>
                    <h4>Specificații Tehnice</h4>
                  </div>
                  <div className="card-body-modern">
                    <div className="specs-grid-modern">
                      <div className="row">
                        <div className="spec-item-modern col-lg-3 col-md-4 col-6">
                          <div className="spec-icon-modern">
                            <i className="feather-box"></i>
                          </div>
                          <div className="spec-info-modern">
                            <span>Caroserie</span>
                            <h6>{car.features?.bodyType}</h6>
                          </div>
                        </div>
                        <div className="spec-item-modern col-lg-3 col-md-4 col-6">
                          <div className="spec-icon-modern">
                            <i className="feather-tag"></i>
                          </div>
                          <div className="spec-info-modern">
                            <span>Marca</span>
                            <h6>{car.make}</h6>
                          </div>
                        </div>
                        <div className="spec-item-modern col-lg-3 col-md-4 col-6">
                          <div className="spec-icon-modern">
                            <i className="feather-settings"></i>
                          </div>
                          <div className="spec-info-modern">
                            <span>Transmisie</span>
                            <h6>{car.transmission}</h6>
                          </div>
                        </div>
                        <div className="spec-item-modern col-lg-3 col-md-4 col-6">
                          <div className="spec-icon-modern">
                            <i className="feather-droplet"></i>
                          </div>
                          <div className="spec-info-modern">
                            <span>Combustibil</span>
                            <h6>{car.features?.fuelType}</h6>
                          </div>
                        </div>
                        <div className="spec-item-modern col-lg-3 col-md-4 col-6">
                          <div className="spec-icon-modern">
                            <i className="feather-activity"></i>
                          </div>
                          <div className="spec-info-modern">
                            <span>Kilometraj</span>
                            <h6>{car.features?.mileage} km</h6>
                          </div>
                        </div>
                        <div className="spec-item-modern col-lg-3 col-md-4 col-6">
                            <div className="spec-icon-modern">
                                <i className="feather-zap"></i>
                            </div>
                            <div className="spec-info-modern">
                                <span>Putere</span>
                                <h6>{car.features?.power} CP</h6>
                            </div>
                        </div>
                        <div className="spec-item-modern col-lg-3 col-md-4 col-6">
                            <div className="spec-icon-modern">
                                <i className="feather-layers"></i>
                            </div>
                            <div className="spec-info-modern">
                                <span>Motor</span>
                                <h6>{car.features?.engineSize} cm³</h6>
                            </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="description-card-modern" data-aos="fade-up" data-aos-delay="200">
                  <div className="card-header-modern">
                    <i className="feather-file-text"></i>
                    <h4>Descriere</h4>
                  </div>
                  <div className="card-body-modern">
                    <p style={{ whiteSpace: 'pre-wrap' }}>{car.description}</p>
                  </div>
                </div>
              </div>
              
              {/* Sidebar Inquiry Form */}
              <div className="col-lg-4">
                <div className="sticky-sidebar-modern" data-aos="fade-left" data-aos-delay="100">
                  <div className="inquiry-card-modern">
                    <div className="price-section-modern">
                      <div className="price-breakdown-modern">
                        {/* FULL VAT REGIME - Show detailed calculation */}
                        {car.vatRegime === 'FULL' ? (
                          <>
                            <div className="price-row-base-premium">
                              <span>Preț net (fără TVA)</span>
                              <span className="price-base-value">€{car.finalPrice?.toLocaleString() || '0'}</span>
                            </div>

                            <div className="price-divider"></div>

                            <div className="price-row-vat-premium">
                              <div className="vat-label-container">
                                <span>TVA deductibil</span>
                                <span className="vat-badge-deductible">{car.vatRate}%</span>
                              </div>
                              <span className="vat-amount-value">+€{car.vatAmount?.toLocaleString() || '0'}</span>
                            </div>

                            <div className="price-divider-bold"></div>

                            <div className="price-row-total-premium">
                              <span>Preț Total</span>
                              <span className="price-total">€{car.priceWithVat?.toLocaleString() || '0'}</span>
                            </div>
                          </>
                        ) : (
                          /* MARGIN VAT REGIME - Show only final price with note */
                          <>
                            <div className="price-row-total-premium">
                              <span>Preț Final</span>
                              <span className="price-total">€{car.priceWithVat?.toLocaleString() || '0'}</span>
                            </div>

                            <div className="vat-included-notice">
                              <i className="feather-info"></i>
                              <span>TVA inclus și nedeductibil</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="divider-modern"></div>

                    <div className="cta-section-modern">
                      <h4>Interesat de această mașină?</h4>
                      <p>Sună acum pentru mai multe detalii!</p>

                      <a href="tel:+40774555555" className="btn-call-primary-modern">
                        <i className="feather-phone"></i>
                        <div className="btn-call-content">
                          <span className="btn-call-label">Sună Acum</span>
                          <span className="btn-call-number">+40 774 555 555</span>
                        </div>
                      </a>

                      <div className="or-divider-modern">
                        <span>sau</span>
                      </div>

                      <button
                        type="button"
                        className="btn-form-toggle-modern"
                        onClick={() => {
                          const form = document.querySelector('.inquiry-form-modern');
                          form?.classList.toggle('expanded');
                        }}
                      >
                        <i className="feather-message-circle"></i>
                        Trimite Mesaj
                      </button>

                      <form onSubmit={submitInquiry} className="inquiry-form-modern">
                        <div className="form-group-modern">
                          <label><i className="feather-user"></i> Numele Tău</label>
                          <input
                            type="text"
                            className="input-modern"
                            placeholder="Ex: Popescu Ion"
                            name="clientName"
                            value={inquiryData.clientName}
                            onChange={handleInquiryChange}
                            required
                          />
                        </div>

                        <div className="form-group-modern">
                          <label><i className="feather-phone"></i> Telefon</label>
                          <input
                            type="tel"
                            className="input-modern"
                            placeholder="Ex: 0712345678"
                            name="clientPhone"
                            value={inquiryData.clientPhone}
                            onChange={handleInquiryChange}
                            required
                          />
                        </div>

                        <div className="form-group-modern">
                          <label><i className="feather-edit"></i> Mesaj (opțional)</label>
                          <textarea
                            className="input-modern textarea-modern"
                            placeholder="Sunt interesat de această mașină..."
                            name="message"
                            value={inquiryData.message}
                            onChange={handleInquiryChange}
                            rows={4}
                          />
                        </div>

                        <button
                          className="btn-submit-modern"
                          type="submit"
                          disabled={submitting}
                        >
                          {submitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Se trimite...
                            </>
                          ) : (
                            <>
                              <i className="feather-send"></i>
                              Trimite Solicitare
                            </>
                          )}
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
    </div>
  );
};

export default ListingDetails;
